import { createFileRoute } from '@tanstack/react-router';
import { SearchEmbed as SearchEmbedType } from '@thoughtspot/visual-embed-sdk';
import { SearchEmbed } from '@thoughtspot/visual-embed-sdk/react';
import { useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { RefObject } from 'react';
import { searchConfig } from '@/customizations/thoughtspotCustomizations';
import { useUserAppViewConfig } from '@/api/queries/useSdkTokenQuery';

/**
 * PRIMARY ROUTE: ThoughtSpot Search (No Data Source)
 *
 * This is the primary search interface where users can search across all available
 * data sources. This route uses a direct SearchEmbed component instead of the
 * prerendered embed pattern used by other ThoughtSpot routes.
 *
 * Why Direct Embed?
 * Search requires special configuration that differs from other ThoughtSpot pages:
 * - `enableCustomColumnGroups`: true (search-specific feature)
 * - `dataPanelV2`: true (enhanced data panel for search)
 * - Dynamic `hiddenActions` from user config (role-based permissions)
 *
 * These requirements don't fit the shared prerendered embed config, so search
 * creates its own independent iframe.
 *
 * URL Pattern: /dashboard/search (no guid = primary search)
 *
 * Compare with Deep Link Search:
 * - Primary Search (this route): No guid, searches all data sources
 * - Deep Link Search (`_deep-links/search.$guid.tsx`): Has guid, pre-selects data source
 *
 * Architecture Note:
 * Located in `_primary-routes/` to indicate this is a primary navigation destination
 * that doesn't use the prerendered embed pattern from `_thoughtspot/`.
 */
export const Route = createFileRoute('/dashboard/_primary-routes/search')({
  component: RouteComponent,
});

function RouteComponent() {
  const ref = useEmbedRef() as RefObject<SearchEmbedType>;
  const userAppViewConfig = useUserAppViewConfig();

  return (
    <SearchEmbed
      ref={ref}
      className="h-[calc(100vh-var(--dashboard-menu-height))] w-full"
      {...searchConfig}
      {...(userAppViewConfig?.hiddenActions && {
        hiddenActions: userAppViewConfig.hiddenActions,
      })}
    />
  );
}
